import React, { useCallback, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftIcon, CalendarPlusIcon, ChevronLeftIcon, ChevronRightIcon, EllipsisIcon, MapPinIcon, PencilIcon, UploadIcon, UserMinusIcon } from "lucide-react";
import type { IconComponent } from "../../types/ui";
import { useClickOutside } from "../../hooks/useClickOutside";
import { Employee, ProfileTab } from "../../types/hr";
import { displayName } from "../../utils/employees";
import { Avatar } from "../ui/Avatar";
export type ProfileAction = 'edit' | 'timeoff' | 'upload' | 'terminate';
interface ProfileHeaderProps {
  employee: Employee;
  position: number;
  total: number;
  tab: ProfileTab;
  onTabChange: (tab: ProfileTab) => void;
  onPrev: () => void;
  onNext: () => void;
  onBack: () => void;
  onAction: (action: ProfileAction) => void;
}
export const PROFILE_TABS: {
  id: ProfileTab;
  label: string;
}[] = [{
  id: 'personal',
  label: 'Personal'
}, {
  id: 'job',
  label: 'Job'
}, {
  id: 'timeoff',
  label: 'Time Off'
}, {
  id: 'documents',
  label: 'Documents'
}];
const ACTIONS: {
  id: ProfileAction;
  label: string;
  icon: IconComponent;
  danger?: boolean;
}[] = [{
  id: 'edit',
  label: 'Edit',
  icon: PencilIcon
}, {
  id: 'timeoff',
  label: 'Request time off',
  icon: CalendarPlusIcon
}, {
  id: 'upload',
  label: 'Upload document',
  icon: UploadIcon
}, {
  id: 'terminate',
  label: 'Terminate',
  icon: UserMinusIcon,
  danger: true
}];
export function ProfileHeader({
  employee,
  position,
  total,
  tab,
  onTabChange,
  onPrev,
  onNext,
  onBack,
  onAction
}: ProfileHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useClickOutside(menuRef, closeMenu, menuOpen);
  const name = displayName(employee);
  return <div className="relative rounded-3xl bg-gradient-to-r from-navy via-[#0a2c6b] to-deep text-white shadow-lift" data-id="element-364" data-mp="20cpbcf60tcmi">
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl" aria-hidden="true" data-id="element-365" data-mp="33n33iuag6k2n">
        <div className="absolute -right-16 -top-24 h-72 w-72 rounded-full bg-white/[0.04]" data-id="element-366" data-mp="2yptevguvucfv" />
        <div className="absolute -bottom-32 right-40 h-64 w-64 rounded-full bg-azure/10" data-id="element-367" data-mp="3cot1yk2qyj9x" />
      </div>

      <div className="relative px-6 pt-5 md:px-8" data-id="element-368" data-mp="37oeub3525bcf">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-369" data-mp="37rsxgdljpokx">
          <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-medium text-white/70 transition hover:text-white" data-id="element-370" data-mp="3mkw6txozgw0k">
            <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-371" data-mp="3vfahto2hdkdv" />
            Employees
          </button>
          <div className="flex items-center gap-2" data-id="element-372" data-mp="3rcxfzaquu6md">
            <span className="hidden text-xs text-white/60 sm:inline" data-id="element-373" data-mp="3rfjww0zmxyh9">
              <span className="font-semibold text-white" data-id="element-374" data-mp="98m0mk7t0y4b">{position}</span> of {total}
            </span>
            <div className="flex overflow-hidden rounded-lg ring-1 ring-inset ring-white/20" data-id="element-375" data-mp="4a2as8q3b26o">
              <button type="button" onClick={onPrev} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white/85 transition hover:bg-white/10" data-id="element-376" data-mp="nvui22rphzoq">
                <ChevronLeftIcon className="h-3.5 w-3.5" data-id="element-377" data-mp="dwv9slaus629" /> Previous
              </button>
              <span className="w-px bg-white/20" data-id="element-378" data-mp="iux11tifnbot" />
              <button type="button" onClick={onNext} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white/85 transition hover:bg-white/10" data-id="element-379" data-mp="itllyxjjehie">
                Next <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-380" data-mp="9vjqsymhij31" />
              </button>
            </div>
            <div ref={menuRef} className="relative" data-id="element-381" data-mp="3rckqspeoaqu4">
              <button type="button" onClick={() => setMenuOpen(o => !o)} aria-haspopup="menu" aria-expanded={menuOpen} aria-label="More actions" className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 ring-1 ring-inset ring-white/20 transition hover:bg-white/20" data-id="element-382" data-mp="4yksq4heca3">
                <EllipsisIcon className="h-4 w-4" data-id="element-383" data-mp="3mdzrqtb5qd79" />
              </button>
              <AnimatePresence data-id="element-384" data-mp="3f8fbjnxxo6nn">
                {menuOpen && <motion.ul role="menu" initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} transition={{
                duration: 0.12
              }} className="absolute right-0 top-10 z-30 w-52 overflow-hidden rounded-xl bg-white py-1.5 text-slate-700 shadow-lift ring-1 ring-slate-900/5" data-id="element-385" data-mp="3aa5jvnbf3o6s">
                    {ACTIONS.map(({
                  id,
                  label,
                  icon: Icon,
                  danger
                }) => <li key={id} role="none" data-id="element-386" data-mp="2hlzwstww5n3s">
                        {danger && <div className="my-1.5 h-px bg-slate-100" data-id="element-387" data-mp="2hnkrcjj7h61n" />}
                        <button type="button" role="menuitem" onClick={() => {
                    setMenuOpen(false);
                    onAction(id);
                  }} className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-sm transition ${danger ? 'text-rose-600 hover:bg-rose-50' : 'hover:bg-canvas'}`} data-id="element-388" data-mp="kseld3b7v9e1">
                          <Icon className="h-4 w-4 opacity-70" data-id="element-389" data-mp="sacrqyy3q4d1" />
                          {label}
                        </button>
                      </li>)}
                  </motion.ul>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-center" data-id="element-390" data-mp="5d1e1tpdezqj">
          <Avatar name={name} seed={employee.id} size="xl" className="ring-4 ring-white/15" data-id="element-391" data-mp="9d1t9gkaneyn" />
          <div className="min-w-0" data-id="element-392" data-mp="ec4zh4c0fr87">
            <h1 className="text-2xl font-semibold tracking-tight md:text-3xl" data-id="element-393" data-mp="ebwhwz4xzxqo">{name}</h1>
            <p className="mt-1 text-sm text-white/75" data-id="element-394" data-mp="jcsqvr45ia2y">
              {employee.jobTitle} <span className="text-white/40" data-id="element-395" data-mp="1psctuihafw8s">·</span> {employee.department}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-id="element-396" data-mp="1uqo60zv22iu6">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 ring-1 ring-inset ring-white/15" data-id="element-397" data-mp="1usr4876d3i9z">
                <span className={`h-1.5 w-1.5 rounded-full ${employee.status === 'Active' ? 'bg-emerald-400' : employee.status === 'Terminated' ? 'bg-slate-400' : 'bg-amber-400'}`} data-id="element-398" data-mp="104h3j6cdeizn" />
                {employee.status}
              </span>
              <span className="rounded-full bg-white/10 px-2.5 py-1 ring-1 ring-inset ring-white/15" data-id="element-399" data-mp="1a2n51mu9ln3z">{employee.employmentType}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 ring-1 ring-inset ring-white/15" data-id="element-400" data-mp="hd7x55xwcaca">
                <MapPinIcon className="h-3 w-3" data-id="element-401" data-mp="w35o1tn7ne56" /> {employee.location}
              </span>
            </div>
          </div>
        </div>

        <nav className="mt-7 flex gap-1 overflow-x-auto" role="tablist" aria-label="Profile sections" data-id="element-402" data-mp="ra2p15znzchl">
          {PROFILE_TABS.map(t => {
          const active = t.id === tab;
          return <button key={t.id} type="button" role="tab" aria-selected={active} onClick={() => onTabChange(t.id)} className={`relative whitespace-nowrap px-4 pb-4 pt-2 text-sm font-medium transition ${active ? 'text-white' : 'text-white/60 hover:text-white'}`} data-id="element-403" data-mp="3urkxnfn9gnj2">
                {t.label}
                {active && <motion.span layoutId="profile-tab" className="absolute inset-x-3 bottom-0 h-[3px] rounded-t-full bg-flame" data-id="element-404" data-mp="20dgw3n1d89ro" />}
              </button>;
        })}
        </nav>
      </div>
    </div>;
}